import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AppSettings } from '../app.settings';
import { ToastrService } from 'ngx-toastr';

import { Response } from '../models/response.model';
import { User } from '../models/user.model';
import {
  LocalStorageService,
  U_SSO,
  U_SSO_URL,
  U_TOKEN,
  U_UTC_OFFSET,
} from './local-storage.service';
import { isEmptyArray } from '../shared/utils';
import { ConfigService } from './config.service';
import { Order } from '../models/order.model';
import { QUOTE_STATE } from '../constants/order-states';
import { Address } from '../models/address.model';
import { GoogleAnalytics } from './google-analytics.service';

const ORDERING_ROLE = 'ORDERING_ROLE';
const AUTOSHIP_ROLE = 'AUTOSHIP_ROLE';
const AUTOSHIP_WSLR_ROLE = 'AUTOSHIP_WSLR_ROLE';
const WSLR_ORDER_VIEW_ROLE = 'WSLR_ORDER_VIEW_ROLE';
const WSLR_ORDER_EDIT_ROLE = 'WSLR_ORDER_EDIT_ROLE';
const WSLR_ADDR_VIEW_ROLE = 'WSLR_ADDR_VIEW_ROLE';
const WSLR_ADDR_EDIT_ROLE = 'WSLR_ADDR_EDIT_ROLE';
const WSLR_ADDR_DEFAULT_ROLE = 'WSLR_ADDR_DEFAULT_ROLE';
const IMP_ORDER_VIEW_ROLE = 'IMP_ORDER_VIEW_ROLE';
const IMP_ORDER_EDIT_ROLE = 'IMP_ORDER_EDIT_ROLE';

const OD_ONLY_ROLE = 'OD_ONLY_ROLE';
const SAP_ROLE = 'SAP_ROLE';
const DECISION_POINTS_ROLE = 'DECISION_POINTS_ROLE';
const CC_PAYMENTS_ROLE = 'CC_PAYMENTS_ROLE';
const ADMIN_REPORTING_ROLE = 'ADMIN_REPORTING_ROLE';
const REPORTING_ROLE = 'REPORTING_ROLE';
const WSLR_REPORTING_ROLE = 'WSLR_REPORTING_ROLE';
const VENDOR_REPORT_ROLE = 'VENDOR_REPORT_ROLE';

const EMPLOYEE_ROLE = 'EMPLOYEE_ROLE';
const EMPLOYEE_ADMIN_ROLE = 'EMPLOYEE_ADMIN_ROLE';
const EMPLOYEE_LIMITED_ROLE = 'EMPLOYEE_LIMITED_ROLE';
const WHOLESALER_ROLE = 'WHOLESALER_ROLE';
const WHOLESALER_ADMIN_ROLE = 'WHOLESALER_ADMIN_ROLE';
const WHOLESALER_LIMITED_ROLE = 'WHOLESALER_LIMITED_ROLE';
const VENDOR_ROLE = 'VENDOR_ROLE';
const VENDOR_ADMIN_ROLE = 'VENDOR__ADMIN_ROLE';
const VENDOR_LIMITED_ROLE = 'VENDOR_LIMITED_ROLE';

const LIMITED_USER_ROLE = 'LIMITED_USER_ROLE';
const ORDERING_USER_ROLE = 'ORDERING_USER_ROLE';
const ORDER_ADMIN_ROLE = 'ORDER_ADMIN_ROLE';
// if (this.onlyRole('LIMITED_USER_ROLE') === true) {
//     this.isLimited = true;
// } else if (this.onlyRole('ORDERING_USER_ROLE') === true) {
//     this.isWholeSeller = true;
// } else if (this.hasRole('ORDER_ADMIN_ROLE') === true) {
//     this.isEmployee = true;
// } else if (this.hasRole('IMP_ORDER_VIEW_ROLE') === true) {
//     this.isWholeSellerMultiple = true;
// }

@Injectable()
export class AuthService extends BaseService {
  public user: User;

  private _canOrder: boolean;
  private _canSeeBuyingWindows: boolean;
  private _canCustomRequest: boolean;
  private _canAutoshipWSLR: boolean;
  private _canEditSap: boolean;
  private _canEditDecisionPoints: boolean;
  private _canEditCreditCards: boolean;
  private _canSeeReports: boolean;
  private _canSeeAddresses: boolean;
  private _canEditAddresses: boolean;
  private _canSetDefaultAddresses: boolean;

  private _isEmployee: boolean;
  private _isWholesaler: boolean;
  private _isWholeSalerMultiple: boolean;
  private _isWholeSalerEditor: boolean;
  private _isVendor: boolean;

  constructor(
    protected http: HttpClient,
    protected appSettings: AppSettings,
    protected toastr: ToastrService,
    private localStorageService: LocalStorageService,
    private googleAnalytics: GoogleAnalytics,
    private config: ConfigService
  ) {
    super('/account', http, appSettings, toastr);
    console.log('AuthService constructor');
  }

  validate(): Observable<User> {
    const token: string = this.localStorageService.getItem(U_TOKEN);
    if (!token) {
      return of(null);
    }

    return this.http.get<Response>(this.apiURL + '/validate/' + token).pipe(
      map((response) => {
        const result = response as Response;

        if (result.data) {
          this.user = new User(result.data);
          return this.user;
        }

        return null;
      }),
      tap((user) => {
        this.parseRoles(user);

        if (user) {
          this.googleAnalytics.submitUserId(user.email);
        }
      })
    );
  }

  login(credentials: any): Observable<any[] | User> {
    return this.http.post<Response>(this.apiURL + '/login', credentials).pipe(
      map((response) => {
        this.user = new User((response as Response).data);
        this.localStorageService.setItem(U_TOKEN, this.user.token);
        this.localStorageService.setItem(
          U_UTC_OFFSET,
          this.user.timezone_offset.toString()
        );

        if (this.user.sso) {
          this.localStorageService.setItem(U_SSO, 'true');
          this.localStorageService.setItem(U_SSO_URL, this.user.sso_url);
        }

        return this.user;
      }),
      catchError(this.handleError('AuthService::login', null)),
      tap((user) => this.parseRoles(user))
    );
  }

  logout(): Observable<boolean> {
    return this.http.get(this.apiURL + '/logout').pipe(
      map(() => {
        this.googleAnalytics.submitLogout(this.user?.email || '');
        this.localStorageService.removeItem(U_TOKEN);
        this.localStorageService.removeItem(U_SSO);
        if (this.user.sso) {
          document.cookie =
            'sso_user_token=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
          window.location.href = 'https://www.abmarketing.com/'; // TODO migrate to 'u-sso-url'
        }

        this.user = null;
        return true;
      }),
      catchError(this.handleError('AuthService::logout', false)),
      tap((logoutResult) => {
        if (logoutResult) {
          this.resetRoles();
        }
      })
    );
  }

  resetPassword(data: any): Observable<any> {
    return this.http.post(this.apiURL + '/reset-password', data).pipe(
      map((response) => {
        return response;
      }),
      catchError(this.handleError('AuthService::resetPassword', []))
    );
  }

  public get isWholeSaler(): boolean {
    return this._isWholesaler;
  }
  public get isWholeSalerMultiple(): boolean {
    return this._isWholeSalerMultiple;
  }

  public get isOneOfWholesaler(): boolean {
    return this.isWholeSaler || this.isWholeSalerMultiple;
  }

  // permissions
  public get canOrder(): boolean {
    return this._canOrder;
  }

  public get canChangeOrderType(): boolean {
    return this._isEmployee;
  }

  public get canSeeReports(): boolean {
    return this._canSeeReports;
  }

  public get canSeeClearance(): boolean {
    return true;
  }

  public get canSeeTruckGraphics(): boolean {
    return true;
  }

  public get canSeeRepack(): boolean {
    return true;
  }

  public get canAddAddress(): boolean {
    return this._canEditAddresses;
  }

  public canEditAddresses(address: Address): boolean {
    if (!address) {
      return false;
    }

    return (
      this._canEditAddresses &&
      address.editable &&
      (this._canSetDefaultAddresses ||
        address.entity_id === this.user.entity_id)
    );
  }

  public canMoveAddress(address: Address): boolean {
    if (!address || address.is_default) {
      return false;
    }

    if (this._canSetDefaultAddresses) {
      return true;
    }

    const addressEntity = address.entity_id || 0;
    return this._canEditAddresses && addressEntity === this.user.entity_id;
  }

  public get canSeeAddresses(): boolean {
    return this._canSeeAddresses;
  }

  public get canCustomRequest(): boolean {
    return this._canCustomRequest;

    // if (this._canCustomRequest) {
    //   return true;
    // }

    // // check if WSLR user has access to autoship
    // return this.isWholeSaler && this._canAutoshipWSLR;
  }

  public get canAutoshipWSLR(): boolean {
    return this._canAutoshipWSLR;
  }

  public get canBuyingWindow(): boolean {
    return this._canSeeBuyingWindows;
  }

  public get canEditSAP(): boolean {
    return this._canEditSap;
  }

  public get canEditCreditCard(): boolean {
    return this._canEditCreditCards && this.config.isCredCardPaymentActive;
  }

  public get canSeeBudgets(): boolean {
    return this.canOrder && !this.isEmployee;
  }

  public get canSeeTransactions(): boolean {
    return (
      this.canOrder &&
      this._canEditCreditCards &&
      this.config.isCredCardPaymentActive
    );
  }

  public get canEditDecisionPoints(): boolean {
    return this._canEditDecisionPoints;
  }

  public get canSeePaymentMethods(): boolean {
    return (
      this.canOrder &&
      (this._canEditSap ||
        (this._canEditCreditCards && this.config.isCredCardPaymentActive))
    );
  }

  public get canSeeOrders(): boolean {
    return this.canOrder;
  }

  public get authenticated(): boolean {
    return !!this.user;
  }

  public get canSeeFavorites(): boolean {
    return true;
  }

  public get canSeeNotifications(): boolean {
    return this._canOrder;
  }

  public get canSeeOrderSummaries(): boolean {
    return this.isWholeSaler;
  }

  public get canSetDefaultAddresses(): boolean {
    return this._canSetDefaultAddresses;
  }

  public get canSeeRepairRequests(): boolean {
    return true;
  }

  public get canAddRepairRequest(): boolean {
    return true;
  }

  public resetRoles() {
    this._canOrder = false;
    this._canSeeBuyingWindows = false;
    this._canCustomRequest = false;
    this._canAutoshipWSLR = false;
    this._canEditSap = false;
    this._canEditDecisionPoints = false;
    this._canEditCreditCards = false;
    this._canSeeReports = false;
    this._canEditAddresses = false;
    this._canSeeAddresses = false;
  }

  private parseRoles(user: User) {
    this.resetRoles();
    this._isEmployee = this.hasRole(
      user,
      EMPLOYEE_ROLE,
      EMPLOYEE_ADMIN_ROLE,
      EMPLOYEE_LIMITED_ROLE,
      ORDER_ADMIN_ROLE
    );
    this._isWholesaler = this.hasRole(
      user,
      WHOLESALER_ROLE,
      WHOLESALER_ADMIN_ROLE,
      WHOLESALER_LIMITED_ROLE
    );
    this._isWholeSalerMultiple = this.hasRole(
      user,
      IMP_ORDER_VIEW_ROLE,
      IMP_ORDER_EDIT_ROLE
    );
    this._isWholeSalerEditor = this.hasRole(user, WSLR_ORDER_EDIT_ROLE);
    this._isVendor = this.hasRole(
      user,
      VENDOR_ROLE,
      VENDOR_ADMIN_ROLE,
      VENDOR_LIMITED_ROLE
    );

    this._canOrder = this.hasRole(user, ORDERING_ROLE);
    if (
      this.hasRole(
        user,
        LIMITED_USER_ROLE,
        EMPLOYEE_LIMITED_ROLE,
        WHOLESALER_LIMITED_ROLE,
        VENDOR_LIMITED_ROLE
      )
    ) {
      // compatibility with old rights
      this._canOrder = false;
    }

    this._canSeeReports = this.hasRole(
      user,
      ADMIN_REPORTING_ROLE,
      REPORTING_ROLE,
      WSLR_REPORTING_ROLE,
      VENDOR_REPORT_ROLE
    );
    this._canCustomRequest = this.hasRole(user, AUTOSHIP_ROLE);
    this._canAutoshipWSLR = this.hasRole(user, AUTOSHIP_WSLR_ROLE);
    this._canSeeAddresses =
      (this.isEmployee && this._canOrder) ||
      this.hasRole(user, WSLR_ADDR_VIEW_ROLE);
    this._canEditAddresses =
      (this.isEmployee && this._canOrder) ||
      this.hasRole(user, WSLR_ADDR_EDIT_ROLE);
    this._canSetDefaultAddresses =
      (this.isEmployee && this._canOrder) ||
      this.hasRole(user, WSLR_ADDR_DEFAULT_ROLE);
    this._canEditCreditCards = this.hasRole(user, CC_PAYMENTS_ROLE);
    this._canEditDecisionPoints = this.hasRole(user, DECISION_POINTS_ROLE);
    this._canEditSap =
      (this.isEmployee && this._canOrder) || this.hasRole(user, SAP_ROLE);
    this._canSeeBuyingWindows = !this.hasRole(user, OD_ONLY_ROLE);
  }

  private hasRole(user: User, ...roles) {
    if (!user || isEmptyArray(user.role)) {
      return false;
    }

    return (
      user.role.filter((role) => {
        if (roles.includes(role)) {
          return true;
        }
      }).length > 0
    );
  }

  get userRole(): string {
    if (this.user.role_label) {
      return this.user.role_label;
    }
    if (this._isEmployee) {
      return 'Employee';
    } else if (this._isWholesaler) {
      return 'Wholesaler';
    } else if (this._isVendor) {
      return 'Vendor';
    }

    return '';
  }

  // Note - Vendor should be treated as an employee
  public get isEmployee(): boolean {
    return this._isEmployee || this._isVendor;
  }

  public get isAdmin(): boolean {
    if (! this.user?.role_label) {
      return false;
    }
    const currentRole =  this.user?.role_label.toLowerCase();
    return currentRole.includes('admin') && currentRole.includes('employee');
  }

  public get isVendor(): boolean {
    return this._isVendor;
  }
  public canDeleteOrder(order: Order): boolean {
    if (!order || !this.user) {
      return false;
    }

    // user can delete only his own order
    if (order.user_id !== this.user.id) {
      return false;
    }

    if (order.isBranch || order.isCustom) {
      return false;
    }

    // user must by employee for  BW orders or BW order has no items
    if (
      order.isByingWindow &&
      this.isEmployee !== true &&
      order.item_count > 0
    ) {
      return false;
    }

    return true;
  }

  public canEditOrder(order: Order) {
    if (!order) {
      return false;
    }

    if (order.id === 0) {
      return true;
    }

    if (!this.canOrder) {
      return false;
    }

    if (order.state !== QUOTE_STATE) {
      return false;
    }

    if (this.isEmployee) {
      return true;
    } else if (this._isWholesaler) {
      if (order.user_id === this.user.id) {
        return true;
      } else {
        return this._isWholeSalerEditor;
      }
    }

    return false;
  }

  public canEditOrderSettings(order: Order): boolean {
    return this.canEditOrder(order) && order.id > 0;
  }

  public canMakeActiveOrder(order) {
    if (!this.canEditOrder(order)) {
      return false;
    }

    return !order.is_active;
  }

  public canShowRecommends(order: Order): boolean {
    if (!order) {
      return false;
    }

    if (order.isByingWindow) {
      return this.isWholeSaler || order.isWSLR || order.isDecisionPoint;
    }

    return false;
  }
}
